import {
  useChatSocket,
  type UseChatSocket,
} from "@sill/shared/chat/useChatSocket";
import { useCallback } from "react";

import type { GetProToken } from "./useProToken";
import type { ClientFrame, ServerFrame } from "../lib/pro-agent-types";
import { emitProResourcesDirty } from "../lib/pro-resource-dirty";
import { useProChatStore } from "../stores/proChat";
import { useTodayStore } from "../stores/today";

// Thin pro-specific wrapper over the generic `useChatSocket` — mirrors
// `frontend/consumer/src/hooks/useAdvisorSocket.ts` exactly (plan 188 W2-M8,
// task 802), pointed at the pro WS endpoint and the pro chat store.
export type UseProChatSocket = UseChatSocket<ClientFrame>;

type Options = {
  /** Called fresh per connection attempt — never pass a captured token string. */
  getToken: GetProToken;
  enabled?: boolean;
  url?: string;
};

export function refreshProDirtyResources(
  frame: ServerFrame,
  getToken: GetProToken,
) {
  if (frame.type !== "info") return;
  const resources = frame.dirty_resources?.filter(Boolean) ?? [];
  if (resources.length === 0) return;
  emitProResourcesDirty(resources);
  if (!resources.includes("today")) return;
  void useTodayStore
    .getState()
    .refresh(getToken, { ignoreRemovedKeys: ["resources_dirty"] });
}

export function useProChatSocket({
  getToken,
  enabled,
  url,
}: Options): UseProChatSocket {
  const handleServerFrame = useProChatStore((state) => state.handleServerFrame);
  const handleProFrame = useCallback(
    (frame: ServerFrame) => {
      handleServerFrame(frame);
      refreshProDirtyResources(frame, getToken);
    },
    [getToken, handleServerFrame],
  );

  return useChatSocket<ServerFrame, ClientFrame>({
    getToken,
    enabled,
    url,
    defaultPath: "/ws/professional/chat",
    onServerFrame: handleProFrame,
  });
}
